import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Download, Copy, CheckCircle, Eye, EyeOff, ArrowRight, RefreshCw, Shield, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Card, CardContent } from '@/components/ui/card';
import { toast } from '@/hooks/useToast';
import { useLoginActions } from '@/hooks/useLoginActions';
import { nip19 } from 'nostr-tools';
import { cn } from '@/lib/utils';
import { 
  generateMnemonicPhrase,
  privateKeyFromMnemonic,
  mnemonicToWords,
  wordsToMnemonic 
} from '@/lib/nip06';
// qrcode imported lazily in async function

interface MnemonicSignupDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSwitchToNsec: () => void;
}

const MnemonicSignupDialog: React.FC<MnemonicSignupDialogProps> = ({ 
  isOpen, 
  onClose,
  onSwitchToNsec 
}) => {
  const [step, setStep] = useState<'generate' | 'verify' | 'complete'>('generate');
  const [mnemonic, setMnemonic] = useState('');
  const [words, setWords] = useState<string[]>([]);
  const [showMnemonic, setShowMnemonic] = useState(true);
  const [verifyIndices, setVerifyIndices] = useState<number[]>([]);
  const [verifyInputs, setVerifyInputs] = useState<Record<number, string>>({});
  const [isVerified, setIsVerified] = useState(false);
  const login = useLoginActions();

  // Generate new mnemonic
  const handleGenerate = () => {
    const newMnemonic = generateMnemonicPhrase(128); // 12 words
    setMnemonic(newMnemonic);
    setWords(mnemonicToWords(newMnemonic));
    
    // Pick 3 random words for verification
    const indices: number[] = [];
    while (indices.length < 3) {
      const rand = Math.floor(Math.random() * 12);
      if (!indices.includes(rand)) indices.push(rand);
    }
    setVerifyIndices(indices.sort((a, b) => a - b));
    
    toast({
      title: 'Recovery Phrase Generated! 🎉',
      description: 'Write down these 12 words in order. They\'re the key to your account!',
    });
  };

  // Download mnemonic as text file
  const handleDownload = () => {
    try {
      const blob = new Blob([mnemonic], { type: 'text/plain; charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'nostr-recovery-phrase.txt';
      a.style.display = 'none';
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast({
        title: 'Recovery Phrase Saved!',
        description: 'Keep this file in a secure location.',
      });
    } catch {
      toast({
        title: 'Download failed',
        description: 'Could not download the file. Please copy manually.',
        variant: 'destructive',
      });
    }
  };

  // Copy to clipboard
  const handleCopy = () => {
    navigator.clipboard.writeText(mnemonic);
    toast({
      title: 'Copied to clipboard!',
      description: 'Your recovery phrase has been copied.',
    });
  };

  // Generate QR code
  const handleQR = async () => {
    try {
      const { default: QRCode } = await import('qrcode');
      const qrDataUrl = await QRCode.toDataURL(mnemonic, {
        width: 300,
        margin: 2,
      });
      
      // Create a new window with the QR code
      const win = window.open('', '_blank');
      if (win) {
        win.document.write(`
          <html>
            <head>
              <title>Recovery Phrase QR Code</title>
              <style>
                body {
                  display: flex;
                  flex-direction: column;
                  align-items: center;
                  justify-content: center;
                  min-height: 100vh;
                  margin: 0;
                  font-family: system-ui, sans-serif;
                  background: #000;
                  color: #fff;
                }
                h1 { margin-bottom: 1rem; }
                img { border: 8px solid #fff; border-radius: 8px; }
                p { margin-top: 1rem; color: #ff0040; font-weight: bold; }
              </style>
            </head>
            <body>
              <h1>🔐 Your Recovery Phrase QR Code</h1>
              <img src="${qrDataUrl}" alt="Recovery Phrase QR Code" />
              <p>⚠️ Keep this QR code private and secure!</p>
              <button onclick="window.print()" style="margin-top: 1rem; padding: 0.5rem 1rem; font-size: 16px; cursor: pointer;">
                Print QR Code
              </button>
            </body>
          </html>
        `);
        win.document.close();
      }
    } catch {
      toast({
        title: 'QR Code failed',
        description: 'Could not generate QR code.',
        variant: 'destructive',
      });
    }
  };

  // Proceed to verification
  const handleProceedToVerify = () => {
    setStep('verify');
  };

  // Verify the words
  const handleVerify = () => {
    const isCorrect = verifyIndices.every((idx) => {
      return verifyInputs[idx]?.toLowerCase().trim() === words[idx];
    });

    if (isCorrect) {
      setIsVerified(true);
      // Log in with the mnemonic
      try {
        const privateKey = privateKeyFromMnemonic(mnemonic);
        const nsec = nip19.nsecEncode(privateKey);
        login.nsec(nsec);
        setStep('complete');
        
        toast({
          title: 'Account Created! 🎉',
          description: 'You\'re all set! Your recovery phrase is secure.',
        });
        
        // Mark signup as completed
        localStorage.setItem('signup_completed', Date.now().toString());
        
        setTimeout(() => {
          onClose();
        }, 2000);
      } catch {
        toast({
          title: 'Login Failed',
          description: 'Failed to create account. Please try again.',
          variant: 'destructive',
        });
      }
    } else {
      toast({
        title: 'Verification Failed',
        description: 'The words don\'t match. Please check and try again.',
        variant: 'destructive',
      });
    }
  };

  // Reset and start over
  const handleStartOver = () => {
    setMnemonic('');
    setWords([]);
    setVerifyInputs({});
    setIsVerified(false);
    setStep('generate');
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-gradient-to-br from-gray-900 to-black border-2 border-[#ccff00]/30">
        <DialogHeader>
          <DialogTitle className="text-2xl font-black uppercase text-center">
            <span className="neon-orange">Create Account with</span>{' '}
            <span className="neon-lime">Recovery Phrase</span>
          </DialogTitle>
        </DialogHeader>

        {/* STEP 1: Generate & Backup */}
        {step === 'generate' && (
          <div className="space-y-6">
            {!mnemonic ? (
              <Card className="bg-black/50 border-[#ccff00]/20">
                <CardContent className="p-8 text-center space-y-4">
                  <div className="text-6xl mb-4">🔑</div>
                  <h3 className="text-xl font-bold text-[#ccff00]">
                    12-Word Recovery Phrase
                  </h3>
                  <p className="text-gray-400 text-sm max-w-md mx-auto">
                    Your recovery phrase is like a master password. Write it down on paper and keep it safe. 
                    You'll need it to access your account from other devices or if you clear your browser data.
                  </p>
                  <Button
                    onClick={handleGenerate}
                    className="w-full max-w-xs bg-gradient-to-r from-[#ff0040] to-[#ff6600] hover:from-[#ff0040]/90 hover:to-[#ff6600]/90 font-bold uppercase"
                    size="lg"
                  >
                    Generate Recovery Phrase
                  </Button>
                  <div className="pt-4">
                    <button
                      onClick={onSwitchToNsec}
                      className="text-xs text-gray-500 hover:text-gray-300 underline"
                    >
                      Use advanced nsec key instead
                    </button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                {/* Recovery phrase display */}
                <Card className="bg-black/50 border-[#ccff00]/30">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="font-bold text-[#ccff00] uppercase">Your Recovery Phrase</h3>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setShowMnemonic(!showMnemonic)}
                          className="text-gray-400 hover:text-white"
                        >
                          {showMnemonic ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleStartOver}
                          className="text-gray-400 hover:text-white"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      {words.map((word, idx) => (
                        <div
                          key={idx}
                          className={cn(
                            "flex items-center gap-2 p-3 rounded-lg border-2",
                            "bg-gray-900 border-[#ccff00]/20"
                          )}
                        >
                          <span className="text-xs text-gray-500 font-mono">{idx + 1}.</span>
                          <span className="font-mono text-sm text-white">
                            {showMnemonic ? word : '••••••'}
                          </span>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-wrap gap-2 justify-center">
                      <Button
                        onClick={handleCopy}
                        variant="outline"
                        size="sm"
                        className="border-[#ccff00]/50 text-[#ccff00] hover:bg-[#ccff00]/10"
                      >
                        <Copy className="h-4 w-4 mr-2" />
                        Copy
                      </Button>
                      <Button
                        onClick={handleDownload}
                        variant="outline"
                        size="sm"
                        className="border-[#ccff00]/50 text-[#ccff00] hover:bg-[#ccff00]/10"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Download
                      </Button>
                      <Button
                        onClick={handleQR}
                        variant="outline"
                        size="sm"
                        className="border-[#ccff00]/50 text-[#ccff00] hover:bg-[#ccff00]/10"
                      >
                        QR Code
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* Warning */}
                <Card className="bg-[#ff0040]/10 border-2 border-[#ff0040]/50">
                  <CardContent className="p-6">
                    <h3 className="font-bold text-[#ff0040] mb-3 uppercase flex items-center gap-2">
                      <span>⚠️</span> Critical Security Warning
                    </h3>
                    <ul className="space-y-2 text-sm text-gray-300">
                      <li>✍️ <strong>Write it down</strong> on paper and store it safely</li>
                      <li>🔒 <strong>Never share</strong> your recovery phrase with anyone</li>
                      <li>💾 <strong>Keep multiple backups</strong> in different secure locations</li>
                      <li>❌ <strong>Anyone with this phrase</strong> controls your account forever</li>
                      <li>🚫 <strong>If you lose it</strong>, you lose your account permanently</li>
                    </ul>
                  </CardContent>
                </Card>

                {/* Continue button */}
                <Button
                  onClick={handleProceedToVerify}
                  className="w-full bg-gradient-to-r from-[#ccff00] to-[#00ff00] hover:from-[#ccff00]/90 hover:to-[#00ff00]/90 text-black font-bold uppercase"
                  size="lg"
                >
                  I've Saved It Securely
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </>
            )}
          </div>
        )}

        {/* STEP 2: Verify */}
        {step === 'verify' && (
          <div className="space-y-6">
            <Card className="bg-black/50 border-[#ccff00]/30">
              <CardContent className="p-6">
                <h3 className="font-bold text-[#ccff00] mb-4 uppercase text-center">
                  Verify Your Recovery Phrase
                </h3>
                <p className="text-gray-400 text-sm mb-6 text-center">
                  To make sure you've saved it correctly, please enter these words:
                </p>

                <div className="space-y-4 max-w-md mx-auto">
                  {verifyIndices.map((idx) => (
                    <div key={idx} className="space-y-2">
                      <label className="text-sm text-gray-300 font-mono">
                        Word #{idx + 1}
                      </label>
                      <input
                        type="text"
                        value={verifyInputs[idx] || ''}
                        onChange={(e) => setVerifyInputs({ ...verifyInputs, [idx]: e.target.value })}
                        placeholder={`Enter word #${idx + 1}`}
                        className="w-full px-4 py-3 rounded-lg bg-gray-900 border-2 border-[#ccff00]/20 text-white placeholder-gray-600 focus:border-[#ccff00]/50 focus:outline-none font-mono"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex gap-3">
              <Button
                onClick={() => setStep('generate')}
                variant="outline"
                className="flex-1 border-gray-600 text-gray-400 hover:bg-gray-800"
              >
                Go Back
              </Button>
              <Button
                onClick={handleVerify}
                disabled={!verifyIndices.every((idx) => verifyInputs[idx]?.trim())}
                className="flex-1 bg-gradient-to-r from-[#ccff00] to-[#00ff00] hover:from-[#ccff00]/90 hover:to-[#00ff00]/90 text-black font-bold uppercase"
              >
                Verify & Create Account
              </Button>
            </div>
          </div>
        )}

        {/* STEP 3: Complete */}
        {step === 'complete' && (
          <div className="py-6 space-y-6">
            <Card className="bg-gradient-to-br from-[#ccff00]/10 to-[#00ff00]/10 border-2 border-[#ccff00]/50">
              <CardContent className="p-8 text-center space-y-4">
                <div className="text-6xl mb-2">✅</div>
                <h3 className="text-2xl font-black uppercase text-[#ccff00]">
                  Account Created!
                </h3>
                <p className="text-gray-300">
                  Your Nostr account is ready to go!
                </p>
                <CheckCircle className="h-12 w-12 mx-auto text-[#00ff00]" />
              </CardContent>
            </Card>
            
            {/* Next Steps - Signer Recommendation */}
            <Card className="bg-blue-950/30 border-2 border-blue-500/30">
              <CardContent className="p-6 space-y-4">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg bg-blue-500/20">
                    <Shield className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h4 className="font-bold text-blue-400">Recommended: Set Up a Signer App</h4>
                    <p className="text-xs text-gray-400">Keep your keys extra secure</p>
                  </div>
                </div>
                <p className="text-sm text-gray-300">
                  For better security, use a dedicated signer app instead of pasting your recovery phrase everywhere. 
                  Signer apps keep your keys safe while letting you use any Nostr app.
                </p>
                <Link to="/signers" onClick={onClose}>
                  <Button className="w-full bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/50">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Learn About Signer Apps
                  </Button>
                </Link>
                <button
                  onClick={onClose}
                  className="w-full text-xs text-gray-500 hover:text-gray-400 py-2"
                >
                  Skip for now
                </button>
              </CardContent>
            </Card>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MnemonicSignupDialog;
